import { Help, Menu as MenuIcon } from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/shared/lib/cx';
import { Button, IconButton, RecordingIndicator } from '@/ui';
import { useSession } from '../AppServicesContext';
import { useActiveRecording } from '../session/ActiveRecordingContext';
import s from '../layouts/Layout.module.css';
import { ClinicSwitcher } from './ClinicSwitcher';
import { useHelp } from './HelpContext';
import { LanguageSwitch } from './LanguageSwitch';
import { Logo } from './Logo';
import { NotificationCenter } from './NotificationCenter';
import { ProfileMenu } from './ProfileMenu';

export interface TopBarProps {
  area: 'clinic' | 'backoffice';
  onOpenNavigation: () => void;
  navigationOpen: boolean;
}

/**
 * Верхняя панель: логотип, переключатель клиники (ПОРТ-16), чип «Демо» у демо-клиники — единственная пометка
 * демо-данных и песочницы на всех страницах (DESIGN.md 1.8), статус записи приёма, «Помощь» (ПОРТ-61),
 * центр уведомлений (ПОРТ-60), язык RU/KK, профиль и выход.
 */
export function TopBar({ area, onOpenNavigation, navigationOpen }: TopBarProps) {
  const { t } = useTranslation('shell');
  const help = useHelp();
  const recording = useActiveRecording();
  const { membership } = useSession();
  return (
    <header className={cx(s.topBar, area === 'backoffice' && s.topBarBackoffice)}>
      <div className={s.brandGroup}>
        <IconButton
          className={s.menuButton}
          label={t('nav.open')}
          icon={MenuIcon}
          aria-expanded={navigationOpen}
          onClick={onOpenNavigation}
          tooltipPlacement="bottom-start"
        />
        <Logo to={area === 'backoffice' ? '/backoffice' : '/'} />
        {area === 'backoffice' ? <span className={s.areaLabel}>{t('area.backoffice')}</span> : null}
      </div>
      {area === 'clinic' ? (
        <div className={s.clinicSlot}>
          <ClinicSwitcher />
          {membership?.isDemo ? (
            <span className={s.demoChip}>
              <span className={s.demoFull}>{t('demo.chip')}</span>
              <span className={s.demoShort} aria-hidden="true">
                {t('demo.short')}
              </span>
            </span>
          ) : null}
        </div>
      ) : null}
      <div className={s.topBarSpacer} />
      {recording ? (
        <RecordingIndicator
          className={s.recordingBar}
          size="compact"
          status={recording.status}
          elapsedSeconds={recording.elapsedSeconds}
          onStop={recording.stop}
          onPause={recording.pause}
          onResume={recording.resume}
        />
      ) : null}
      <div className={s.topBarGroup}>
        <Button variant="ghost" size="sm" className={s.helpButton} onClick={() => help.open()} aria-label={t('help.button')}>
          <Help size={16} aria-hidden="true" className={s.helpButtonIcon} />
          <span className={s.helpButtonLabel}>{t('help.button')}</span>
        </Button>
        <NotificationCenter />
        <LanguageSwitch />
        <ProfileMenu area={area} />
      </div>
    </header>
  );
}
